"use client";

import { useState, useEffect, useRef, useContext } from "react";
import { useToast } from "@/components/ui/toast";
import {
  Menu,
  X,
  MessageSquare,
  User,
  Search,
  ChevronDown,
  Home,
  LogOut,
  Heart,
  UserPlus,
  Settings,
  Camera,
  Mic,
  MicOff,
  ShoppingBag,
  Building2,
  Truck,
  Briefcase,
  Store,
  Star,
  Crown,
  TrendingUp,
  Grid3X3,
} from "lucide-react";
import { IoNotifications } from "react-icons/io5";
import { useSocket } from "../../context/SocketContext";
import merzadologo from "../../images/newnavbarmerzadologo.png";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { CurrencyContext } from "@/context/CurrencyContext";

const SERVICES_THEME = {
  transparent: "bg-transparent",
  solid: "bg-gradient-to-r from-blue-600 via-cyan-500 to-teal-500",
  accent: "text-indigo-100",
};

const LOGISTICS_THEME = {
  transparent: "bg-transparent",
  solid: "bg-gradient-to-br from-orange-500 via-amber-500 to-yellow-500",
  accent: "text-teal-100",
};

const BUSINESS_THEME = {
  transparent: "bg-transparent",
  solid: "bg-gradient-to-r from-emerald-600 to-teal-600",
  accent: "text-green-100",
};

const PRODUCTS_THEME = {
  transparent: " bg-transparent",
  solid: "bg-blue-600 xl:bg-gradient-to-r xl:from-blue-600 xl:to-blue-800",
  accent: "text-blue-100",
};

const FRANCHISE_THEME = {
  transparent: "bg-transparent",
  solid: "bg-gradient-to-r from-indigo-600 to-purple-600",
  accent: "text-purple-100",
};

const SELLER_THEME = {
  transparent: "bg-transparent",
  solid: "bg-gradient-to-r from-blue-600 to-blue-800",
  accent: "text-blue-100",
};

// Page color themes
const PAGE_THEMES = {
  "/": {
    // transparent: " bg-transparent",
    // solid: "bg-gradient-to-r from-blue-600 to-blue-800",
    // accent: "text-blue-100",
    transparent: "hidden sm:block bg-transparent", // transparent only on desktop
    solid: "bg-blue-600 xl:bg-gradient-to-r xl:from-blue-600 xl:to-blue-800", // blue on mobile, gradient on desktop
    accent: "text-blue-100",
  },

  "/products": PRODUCTS_THEME,
  "/products/:productid": PRODUCTS_THEME,

  "/sellers": SELLER_THEME,
  "/sellers/:sellerId": SELLER_THEME,
  // "/sellerdashboard": SELLER_THEME,
  // "/selleronboarding": SELLER_THEME,

  "/localsuppliers": {
    transparent: "bg-transparent",
    solid: "bg-gradient-to-r from-orange-600 to-red-600",
    accent: "text-orange-100",
  },

  "/franchise": FRANCHISE_THEME,
  "/franchise/:franchiseId": FRANCHISE_THEME,
  // "/dashboard/franchise": FRANCHISE_THEME,
  // "/businessfranchiseonboarding": FRANCHISE_THEME,

  "/business": BUSINESS_THEME,
  "/business/:id": BUSINESS_THEME,
  // "/dashboard/business-services": BUSINESS_THEME,
  // "/businessfranchiseonboarding": BUSINESS_THEME,

  "/logistics": LOGISTICS_THEME,
  "/logistics/:id": LOGISTICS_THEME,
  // "/logisticsDashboard": LOGISTICS_THEME,
  // "/logisticsonboarding": LOGISTICS_THEME,

  "/services": SERVICES_THEME,
  "/services/:id": SERVICES_THEME,
  // "/dashboard/business-services": SERVICES_THEME,
  // "/serviceonboarding": SERVICES_THEME,

  default: {
    transparent: "bg-transparent",
    solid: "bg-gradient-to-r from-blue-600 to-blue-800",
    accent: "text-blue-100",
  },
};

const INTERNATIONAL_CURRENCIES = [
  "USD",
  "EUR",
  "JPY",
  "GBP",
  "CHF",
  "CAD",
  "AUD",
  "CNY",
  "NZD",
  "SEK",
  "NOK",
  "DKK",
  "SGD",
  "HKD",
  "INR",
  "KRW",
  "THB",
  "MYR",
  "IDR",
  "PHP",
  "VND",
  "TWD",
  "AED",
  "SAR",
  "QAR",
  "KWD",
  "BHD",
  "OMR",
  "JOD",
  "ILS",
  "PKR",
  "BDT",
  "NPR",
  "LKR",
  "MXN",
  "BRL",
  "CLP",
  "COP",
  "ARS",
  "PEN",
].sort();

// Place major currencies at the top
const MAJOR_CURRENCIES = [
  "INR",
  "USD",
  "EUR",
  "GBP",
  "JPY",
  "CNY",
  "AUD",
  "CAD",
];
const OTHER_CURRENCIES = INTERNATIONAL_CURRENCIES.filter(
  (c) => !MAJOR_CURRENCIES.includes(c),
);
const SORTED_CURRENCIES = [...MAJOR_CURRENCIES, ...OTHER_CURRENCIES];

const SEARCH_CATEGORIES = [
  { value: "all", label: "All Categories", icon: Grid3X3 },
  { value: "products", label: "Products", icon: ShoppingBag },
  { value: "sellers", label: "Sellers", icon: Store },
  {
    value: "franchises",
    label: "Franchise Opportunities",
    icon: Building2,
    isOnboarding: true,
  },
  {
    value: "logistics",
    label: "Logistics Services",
    icon: Truck,
    isOnboarding: true,
  },
  {
    value: "services",
    label: "Business Services",
    icon: Briefcase,
    isOnboarding: true,
  },
  {
    value: "become-seller",
    label: "Become a Seller",
    icon: UserPlus,
    isOnboarding: true,
  },
];

// Mock user onboarding status
const USER_ONBOARDING_STATUS = {
  seller: false,
  logistics: false,
  franchise: false,
  services: false,
  business: false,
};

// Advanced Search Bar Component
const AdvancedSearchBar = ({ currentPath }) => {
  const {
    success: showSuccess,
    error: showError,
    info: showInfo,
    warning: showWarning,
  } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const categories = [
    { label: "All Categories", icon: Grid3X3 },
    { label: "Products", icon: ShoppingBag },
  ];

  const [selectedCategory, setSelectedCategory] = useState(categories[0]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);

  const searchRef = useRef(null);
  const categoryRef = useRef(null);

  const getCurrentTheme = () => {
    // Exact match first
    if (PAGE_THEMES[currentPath]) return PAGE_THEMES[currentPath];

    // Dynamic route matching
    if (currentPath.startsWith("/products/"))
      return PAGE_THEMES["/products/:productid"];
    if (currentPath.startsWith("/sellers/"))
      return PAGE_THEMES["/sellers/:sellerId"];
    if (currentPath.startsWith("/franchise/"))
      return PAGE_THEMES["/franchise/:franchiseId"];
    if (currentPath.startsWith("/business/"))
      return PAGE_THEMES["/business/:id"];
    if (currentPath.startsWith("/logistics/"))
      return PAGE_THEMES["/logistics/:id"];
    if (currentPath.startsWith("/services/"))
      return PAGE_THEMES["/services/:id"];

    return PAGE_THEMES.default;
  };

  const theme = getCurrentTheme();

  // Close suggestions on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch autocomplete suggestions from search API
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    setIsLoading(true);

    const fetchSuggestions = async () => {
      try {
        let suggestionsData = [];

        if (selectedCategory.label === "Products") {
          // Only fetch products when "Products" category is selected
          const productRes = await axios.get(
            `${process.env.REACT_APP_BASE_URL}/search/products`,
            {
              params: {
                autocomplete: true,
                q: searchQuery.trim(),
              },
            },
          );
          suggestionsData = productRes.data || [];
        } else if (selectedCategory.label === "All Categories") {
          // Use the all endpoint when "All Categories" is selected
          const allRes = await axios.get(
            `${process.env.REACT_APP_BASE_URL}/search/all`,
            {
              params: {
                autocomplete: true,

                q: searchQuery.trim(),
              },
            },
          );
          // Combine products and other results from the all endpoint
          const allData = allRes.data || {};
          suggestionsData = [
            ...(allData.products?.hits || []),
            ...(allData.sellers?.hits || []),
            ...(allData.franchises?.hits || []),
            ...(allData.businesses?.hits || []),
            ...(allData.logistics?.hits || []),
            ...(allData.services?.hits || []),
          ].slice(0, 5); // Limit to 5 suggestions for autocomplete
        } else {
          // For other categories, fetch bfss only
          const bfssRes = await axios.get(
            `${process.env.REACT_APP_BASE_URL}/search/bfss`,
            {
              params: {
                autocomplete: true,
                q: searchQuery.trim(),
              },
            },
          );
          suggestionsData = bfssRes.data || [];
        }

        setSuggestions(suggestionsData);
        if (suggestionsData.length > 0) {
          setShowSuggestions(true);
        }
      } catch (error) {
        console.error("Error fetching suggestions:", error);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    };

    const timer = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, selectedCategory]);

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    const title = suggestion?.title;
    const name = suggestion?.name;

    const query = title || name || "";

    console.log("Suggestion clicked:", query); // Debug log
    setSearchQuery(query);
    setShowSuggestions(false);

    // Navigate to search results using the same logic as handleNavigate
    if (query.trim()) {
      const encoded = encodeURIComponent(query.trim());
      const path =
        selectedItem.label === "Products"
          ? `/search/products?query=${encoded}`
          : selectedItem.label === "All Categories"
            ? `/search/categories?query=${encoded}`
            : `/search/bfss?query=${encoded}`;
      console.log("Navigating to:", path);
      window.location.href = path;
    }
  };

  // Handle voice search
  const handleVoiceSearch = () => {
    if ("webkitSpeechRecognition" in window || "SpeechRecognition" in window) {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();

      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = "en-US";

      recognition.onstart = () => {
        setIsListening(true);
      };

      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setSearchQuery(transcript);
        setIsListening(false);
      };

      recognition.onerror = () => {
        setIsListening(false);
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognition.start();
    } else {
      showError(
        "Speech recognition not supported",
        "Your browser doesn't support speech recognition. Please try a different browser.",
      );
    }
  };
  const [category, setCategory] = useState(true);

  const handleNavigate = () => {
    if (!searchQuery.trim()) {
      // alert("Please enter a search term");
      return;
    }
    const encoded = encodeURIComponent(searchQuery.trim());
    const path =
      selectedItem.label === "Products"
        ? `/search/products?query=${encoded}`
        : selectedItem.label === "All Categories"
          ? `/search/categories?query=${encoded}`
          : `/search/bfss?query=${encoded}`;
    console.log("Selected item:", selectedItem.label, "Path:", path);
    window.location.href = path;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleNavigate();
  };

  // Handle image search
  const handleImageSearch = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async (e) => {
      const file = e.target.files[0];

      if (!file) {
        console.warn("No file selected");
        return;
      }

      console.log(
        "🖼️ Image selected for search:",
        file.name,
        "Size:",
        file.size,
        "Type:",
        file.type,
      );

      // Read file as base64 for preview
      const imagePreviewPromise = new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => resolve(event.target.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      try {
        const formData = new FormData();
        formData.append("image", file);

        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/image-search`,
          formData,
          {
            params: {
              page: 1,
              limit: 20,
            },
            // Do NOT set Content-Type header manually - axios will set it automatically with boundary
            withCredentials: true,
            timeout: 60000, // 60 second timeout for image processing
          },
        );

        // Get image preview
        let imagePreview = null;
        try {
          imagePreview = await imagePreviewPromise;
        } catch (previewError) {
          console.warn("Could not read image preview:", previewError);
        }

        // Always navigate to search results page, even if no products found
        // Store the response data (or empty results) with image preview
        const dataToStore = {
          ...(response.data || {}),
          matchCount: response.data?.matchCount || 0,
          matchedProducts: response.data?.matchedProducts || [],
          imagePreview: imagePreview, // Always include image preview
        };

        localStorage.setItem("imageSearchResults", JSON.stringify(dataToStore));
        console.log("💾 Stored results in localStorage, navigating...");

        // Navigate to the search results page with a special query
        window.location.href = `/search/products?query=image-search`;
      } catch (error) {
        console.error("❌ Image search failed:", error);
        console.error("❌ Error response:", error.response?.data);
        console.error("❌ Error status:", error.response?.status);
        console.error("❌ Error message:", error.message);

        // Even on error, try to navigate with the image preview if we have it
        try {
          const imagePreview = await imagePreviewPromise;
          const errorDataToStore = {
            success: false,
            matchCount: 0,
            matchedProducts: [],
            imagePreview: imagePreview,
            error:
              error.response?.data?.message ||
              error.message ||
              "Image search failed",
          };
          localStorage.setItem(
            "imageSearchResults",
            JSON.stringify(errorDataToStore),
          );
          window.location.href = `/search/products?query=image-search`;
        } catch (previewError) {
          if (error.response) {
            showError(
              "Image search failed",
              error.response.data?.message ||
              `Server error: ${error.response.status}`,
            );
          } else if (error.request) {
            showError(
              "Network error",
              "Could not reach the server. Please check your connection.",
            );
          } else {
            showError("Request error", error.message || "Please try again.");
          }
        }
      }
    };
    input.click();
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
      if (categoryRef.current && !categoryRef.current.contains(event.target)) {
        setIsCategoryOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const SelectedIcon = selectedCategory.icon;

  const [isOpenTo, setIsOpenTo] = useState(false);
  // const [searchQuery, setSearchQuery] = useState("");
  // const [showSuggestions, setShowSuggestions] = useState(false);

  const [selectedItem, setSelectedItem] = useState(categories[0]);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpenTo(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const Icon = selectedItem.icon;

  return (
    <div className="py-2 px-0.5 xl:py-4 xl:px-2 sm:p-8 w-full">
      <form onSubmit={handleSubmit}>
        <div className="relative w-full max-w-3xl">
          {/* DROPDOWN MOVED OUTSIDE - This is the key fix */}
          <div className="relative" ref={dropdownRef}>
            {isOpenTo && (
              <div
                className="absolute z-[9999] left-0 top-12 xl:top-16 min-w-[120px] xl:min-w-[150px] bg-white border border-gray-200 rounded-lg shadow-xl"
                style={{
                  backgroundColor: "white",
                  boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
                }}
              >
                {categories.map((item) => {
                  const ItemIcon = item.icon;
                  return (
                    <button
                      key={item.label}
                      onClick={() => {
                        console.log("Item clicked:", item.label);
                        setSelectedItem(item);
                        setCategory(!category);
                        console.log(category);
                        setIsOpenTo(false);
                      }}
                      className={`w-full flex items-center space-x-2 xl:space-x-3 px-3 xl:px-4 py-2 xl:py-3 hover:bg-gray-50 text-left border-b border-gray-100 last:border-b-0 transition-colors ${selectedItem.label === item.label
                          ? "bg-gray-100 text-gray-600"
                          : "text-gray-700"
                        }`}
                    >
                      <ItemIcon
                        className={`h-3 w-3 xl:h-4 xl:w-4 ${selectedItem.label === item.label
                            ? "text-black-600"
                            : "text-gray-600"
                          }`}
                      />
                      <span className="text-xs xl:text-sm font-medium">
                        {item.label}
                      </span>
                      {selectedItem.label === item.label && (
                        <span className="ml-auto text-blue-600 text-xs xl:text-sm">
                          ✓
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* MAIN SEARCH BAR */}
            <div
              ref={searchRef}
              className="flex items-center bg-white/95 backdrop-blur-sm border border-white/30 rounded-full shadow-lg overflow-visible"
            >
              {/* Category Selector Button */}
              <button
                type="button"
                onClick={() => {
                  console.log(
                    "Dropdown button clicked, current state:",
                    isOpenTo,
                  );
                  setIsOpenTo(!isOpenTo);
                }}
                className="flex items-center space-x-1 xl:space-x-2 px-2 xl:px-4 py-2 xl:py-3 transition-colors duration-200 border-r border-gray-200"
              >
                <Icon className="h-3 w-3 xl:h-4 xl:w-4 text-gray-600" />
                <span className="text-xs xl:text-sm font-medium text-gray-700 hidden sm:block">
                  {selectedItem.label}
                </span>
                <ChevronDown
                  className={`h-3 w-3 xl:h-4 xl:w-4 text-gray-500 transition-transform duration-200 ${isOpenTo ? "rotate-180" : ""
                    }`}
                />
              </button>

              {/* Search Input */}
              <div className="flex-1">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    // Don't set showSuggestions here, let useEffect handle it after API call
                  }}
                  onFocus={() => {
                    // Only show suggestions if we have them and there's a query
                    if (searchQuery.length > 0 && suggestions.length > 0) {
                      setShowSuggestions(true);
                    }
                  }}
                  placeholder={`Search ${selectedCategory.label.toLowerCase()}...`}
                  className="w-full px-2 xl:px-4 py-2 xl:py-3 bg-transparent text-gray-900 placeholder-gray-500 focus:outline-none text-sm xl:text-base"
                />
              </div>

              {/* Search Tools */}
              <div className="flex items-center space-x-1 xl:space-x-2 px-2 xl:px-3">
                {/* Voice Search - Now visible on mobile */}
                <button
                  type="button"
                  onClick={handleVoiceSearch}
                  className={`p-1.5 xl:p-2 rounded-full transition-all duration-200 ${isListening
                      ? "bg-red-100 text-red-600 animate-pulse"
                      : "hover:bg-gray-100 text-gray-600"
                    }`}
                  title="Voice Search"
                >
                  {isListening ? (
                    <MicOff className="h-5 w-5 xl:h-5 xl:w-5" />
                  ) : (
                    <Mic className="h-5 w-5 xl:h-5 xl:w-5" />
                  )}
                </button>

                {/* Image Search - Now visible on mobile */}
                <button
                  type="button"
                  onClick={handleImageSearch}
                  className="p-1.5 xl:p-2 rounded-full hover:bg-gray-100 text-gray-600 transition-all duration-200"
                  title="Image Search"
                >
                  <Camera className="h-5 w-5 xl:h-5 xl:w-5" />
                </button>

                {/* Search Button */}
                <button
                  type="submit"
                  onClick={() => {
                    console.log(
                      "Searching for:",
                      searchQuery,
                      "in category:",
                      selectedCategory.value,
                    );
                    setShowSuggestions(false);
                  }}
                  className={`${theme.solid} hover:opacity-90 text-white px-3 xl:px-6 py-1.5 xl:py-2 rounded-full transition-all duration-200 font-medium shadow-md`}
                // className="bg-blue-600 hover:bg-blue-700 text-white px-3 xl:px-6 py-1.5 xl:py-2 rounded-full transition-all duration-200 font-medium shadow-md"
                >
                  <Search className="h-3 w-3 xl:h-4 xl:w-4" />
                </button>
              </div>
            </div>

            {/* Search Suggestions - Positioned outside to align with input field only */}
            {showSuggestions && suggestions.length > 0 && (
              <div
                className="absolute z-[9999] bg-white border border-gray-200 rounded-md shadow-lg max-h-72 overflow-y-auto"
                style={{
                  top: "100%",
                  marginTop: "4px",
                  left: "0px", // Start from the beginning of search container
                  right: "0px", // End at the end of search container
                  minWidth: "300px",
                }}
              >
                {isLoading ? (
                  <div className="px-4 py-3 text-center text-gray-500">
                    Loading suggestions...
                  </div>
                ) : (
                  suggestions.slice(0, 6).map((suggestion, index) => (
                    <div
                      key={index}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex flex-col border-b last:border-b-0"
                      onMouseDown={(e) => {
                        e.preventDefault(); // Prevent the mousedown from bubbling
                        handleSuggestionClick(suggestion);
                      }}
                      tabIndex={0}
                    >
                      <span className="font-medium">
                        {suggestion.title || suggestion.name}
                      </span>
                      {suggestion.pricerange && (
                        <span className="text-xs text-gray-500">
                          Price: {suggestion.pricerange.min} -{" "}
                          {suggestion.pricerange.max}
                        </span>
                      )}
                      {suggestion.variants &&
                        suggestion.variants.length > 0 && (
                          <span className="text-xs text-gray-400">
                            Variants:{" "}
                            {suggestion.variants.map((v) => v.type).join(", ")}
                          </span>
                        )}
                      {suggestion.category && !suggestion.pricerange && (
                        <span className="text-xs text-gray-400 mt-1">
                          {suggestion.category}
                        </span>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

// Google Translate Component
const GoogleTranslate = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState({
    code: "en",
    name: "English",
    flag: "🇺🇸",
  });

  const languages = [
    { code: "en", name: "English", flag: "🇺🇸" },
    { code: "es", name: "Español", flag: "🇪🇸" },
    { code: "fr", name: "Français", flag: "🇫🇷" },
    { code: "de", name: "Deutsch", flag: "🇩🇪" },
    { code: "it", name: "Italiano", flag: "🇮🇹" },
    { code: "zh", name: "中文", flag: "🇨🇳" },
    { code: "ja", name: "日本語", flag: "🇯🇵" },
    { code: "ko", name: "한국어", flag: "🇰🇷" },
    { code: "hi", name: "हिन्दी", flag: "🇮🇳" },
    { code: "ar", name: "العربية", flag: "🇸🇦" },
    { code: "pt", name: "Português", flag: "🇵🇹" },
    { code: "ru", name: "Русский", flag: "🇷🇺" },
  ];

  useEffect(() => {
    const previousOnError = window.onerror;

    window.googleTranslateElementInit = function () {
      if (document.querySelector(".goog-te-gadget")) return;

      try {
        if (
          window.google &&
          window.google.translate &&
          window.google.translate.TranslateElement
        ) {
          new window.google.translate.TranslateElement(
            {
              pageLanguage: "en",
              includedLanguages:
                "hi,ta,te,ml,kn,gu,pa,ur,bn,or,mr,as,sa,ks,sd,mai,ne,doi,brx,gom,ks,ml,kn,gu,pa,ur,bn,or,mr,as,sa,en,af,sq,am,ar,hy,az,eu,be,bg,bs,my,ca,ceb,ny,zh-CN,zh-TW,co,hr,cs,da,nl,eo,et,fi,fr,fy,gl,ka,de,el,ht,ha,haw,iw,he,hmn,hu,is,ig,id,ga,it,ja,jw,kk,km,rw,ko,ku,ky,lo,la,lv,lt,lb,mk,mg,ms,mt,mi,mn,my,ps,fa,pl,pt,ro,ru,sm,gd,sr,st,si,sk,sl,so,es,su,sw,sv,tg,tt,th,tr,tk,uk,ug,uz,vi,cy,xh,yi,yo,zu",
              layout:
                window.google.translate.TranslateElement.InlineLayout.SIMPLE,
            },
            "google_translate_element",
          );
        }
      } catch (e) { }

      window.onerror = function (message, source) {
        if (source && source.includes("translate_a/element.js")) return true;
        if (typeof previousOnError === "function") {
          return previousOnError.apply(this, arguments);
        }
        return false;
      };
    };

    if (!document.querySelector("#google-translate-script")) {
      const addScript = document.createElement("script");
      addScript.id = "google-translate-script";
      addScript.src =
        "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      addScript.async = true;
      document.body.appendChild(addScript);
    }

    return () => {
      window.onerror = previousOnError;
    };
  }, []);

  return (
    <div className="relative">
      {/* Styled container for Google Translate selector */}
      <div
        className="bg-white/90 backdrop-blur-sm hover:bg-white transition-all duration-200 px-2 py-2 rounded-full text-gray-800 font-small flex items-center space-x-1 shadow-md"
        style={{ minWidth: 120, minHeight: 40 }}
      >
        <div id="google_translate_element" />
      </div>
      <style>
        {`
          /* Hide "Powered by Google Translate" and logo in vertical layout */
          .VIpgJd-ZVi9od-l4eHX-hSRGPd,
          .goog-logo-link,
          .skiptranslate.goog-te-gadget > span[style*="white-space:nowrap"],
          .skiptranslate.goog-te-gadget > span:last-child {
            display: none !important;
          }
            .goog-te-gadget-simple {
  display: inline-flex !important;
  align-items: center !important;
  gap: 4px;
  font-family: inherit !important;
  font-size: 14px !important;
  background-color: #e7e7e7 !important;
  border:none !important;
}

.goog-te-combo {
  padding: 6px 10px !important;
  font-size: 14px !important;
  border-radius: 6px !important;
  border: 1px solid #ccc !important;
  background-color: white !important;
  width: auto !important;
}
  .VIpgJd-ZVi9od-xl07Ob-lTBxed {
  display: inline-flex !important;
  align-items: center !important;
  gap: 4px !important; /* Adjust spacing between elements */
  font-size: 14px;
  font-family: inherit;
  color: #333;
  text-decoration: none;
  line-height: 1.2;
}
/* Remove the ▼ down arrow */
.VIpgJd-ZVi9od-xl07Ob-lTBxed span[aria-hidden="true"] {
  display: none !important;
}
  .VIpgJd-ZVi9od-xl07Ob-lTBxed span[style*="border-left"] {
  display: none !important;
}

/* Optional: Make sure surrounding span behaves nicely */
.VIpgJd-ZVi9od-xl07Ob-lTBxed > span,
.VIpgJd-ZVi9od-xl07Ob-lTBxed > img {
  display: inline-block !important;
  vertical-align: middle;
}

        `}
      </style>
    </div>
  );
};

const ImagePlaceHolder = ({
  width,
  height,
  userimage,
  email,
  dropdownOpen = false,
}) => {
  const [imgError, setImgError] = useState(false);
  const showImage = userimage && !imgError;

  return (
    <div
      className={`rounded-full border-2 border-white/30 overflow-hidden hover:border-white/50 transition-all duration-200 flex items-center justify-center
        ${dropdownOpen ? "bg-gray-500" : "bg-white/20 backdrop-blur-sm"}`}
      style={{ width, height }}
    >
      {showImage ? (
        <img
          src={userimage}
          alt="Profile"
          className="w-full h-full object-cover"
          onError={() => setImgError(true)}
        />
      ) : email ? (
        <span className="w-full h-full flex items-center justify-center text-xl font-bold text-white uppercase">
          {email && email.length > 0 ? email[0].toUpperCase() : "?"}
        </span>
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <User className="w-1/2 h-1/2 text-white/70" />
        </div>
      )}
    </div>
  );
};

// Attractive Seller Button Component
const AttractiveSellerButton = ({ onClick }) => {
  return (
    <button
      onClick={onClick}
      className="relative group bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 hover:from-yellow-500 hover:via-orange-600 hover:to-red-600 text-white px-6 py-3 rounded-full font-bold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 overflow-hidden mr-4"
    >
      {/* Animated background */}
      <div className="absolute inset-0 bg-gradient-to-r from-yellow-300 via-orange-400 to-red-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

      {/* Shine effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>

      {/* Content */}
      <div className="relative flex items-center space-x-2  ">
        <Crown className="h-5 w-5 animate-pulse" />
        <span className="text-sm font-bold">Become a Seller</span>
        {/* <Star className="h-4 w-4 animate-bounce" /> */}
      </div>

      {/* Glow effect */}
      <div className="absolute -inset-1 bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 rounded-full blur opacity-30 group-hover:opacity-50 transition-opacity duration-300 -z-10"></div>
    </button>
  );
};

// Onboarding Button Component
const OnboardingButton = ({ type, isOnboarded, onClick }) => {
  const buttonConfigs = {
    seller: {
      text: "Start Seller",
      icon: Store,
      color:
        "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 mr-3",
      component: AttractiveSellerButton,
    },
    homeSpecial: {
      text: "Join Merzado",
      color: "bg-gradient-to-r from-pink-500 to-yellow-500 text-white",
      route: "/joinmerzado",
    },
    logistics: {
      text: "List Logistics",
      icon: Truck,
      color:
        "bg-gradient-to-br from-orange-600 via-amber-550 to-yellow-600 hover:from-teal-700 hover:to-teal-800 mr-3",
    },
    franchise: {
      text: "List Franchise",
      icon: Building2,
      color:
        "bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 mr-3",
    },
    services: {
      text: "List Service",
      icon: Briefcase,
      color:
        "bg-gradient-to-r from-blue-650 via-cyan-550 to-teal-600 hover:from-indigo-700 hover:to-indigo-800 mr-3",
    },
    business: {
      text: "List Your Business",
      icon: TrendingUp,
      color:
        "bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 mr-3",
    },
  };

  const config = buttonConfigs[type];
  const Icon = config?.icon;

  if (isOnboarded) {
    return null;
  }

  if (type === "seller" && config.component) {
    const SpecialComponent = config.component;
    return <SpecialComponent onClick={onClick} />;
  }

  // Add bouncing star to all onboarding buttons
  return (
    <button
      onClick={onClick}
      className={`${config.color} text-white px-4 py-2 rounded-full font-medium transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 text-sm flex items-center space-x-2`}
      style={{ height: "44px", minHeight: "44px" }}
    >
      {Icon && <Icon className="h-4 w-4" />}
      {/* <span>{config.text}</span> */}
      <span className="whitespace-nowrap">{config.text}</span>
      <Star className="h-4 w-4" />
    </button>
  );
};

const currencySymbols = {
  INR: "₹",
  USD: "$",
  EUR: "€",
  JPY: "¥",
  GBP: "£",
  CHF: "Fr.",
  CAD: "C$",
  AUD: "A$",
  CNY: "¥",
  NZD: "NZ$",
  SEK: "kr",
  NOK: "kr",
  DKK: "kr",
  SGD: "S$",
  HKD: "HK$",
  KRW: "₩",
  THB: "฿",
  MYR: "RM",
  IDR: "Rp",
  PHP: "₱",
  VND: "₫",
  TWD: "NT$",
  AED: "د.إ",
  SAR: "ر.س",
  QAR: "ر.ق",
  KWD: "د.ك",
  BHD: "ب.د",
  OMR: "ر.ع.",
  JOD: "د.ا",
  ILS: "₪",
  PKR: "₨",
  BDT: "৳",
  NPR: "₨",
  LKR: "₨",
  MXN: "$",
  BRL: "R$",
  CLP: "$",
  COP: "$",
  ARS: "$",
  PEN: "S/",
};

function getCurrencySymbol(code) {
  return currencySymbols[code] || "";
}

// expoting user role and badge for subscription page - by saurabh
let userRole = null;
let userBadge = null;

export const getUserRole = () => userRole;
export const getUserBadge = () => userBadge;

export default function AdvancedNavbar({
  transparent = false,
  shouldOverlap = false,
}) {
  const {
    success: showSuccess,
    error: showError,
    info: showInfo,
    warning: showWarning,
  } = useToast();
  const { unreadCount: unreadChatCount } = useSocket() || {}; // Get global unread chat count
  const [scrolled, setScrolled] = useState(false);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userprofile, setUserProfile] = useState({
    name: "John Doe",
    profilepic: null,
  });
  const { selectedCurrency, setSelectedCurrency, convertPrice } =
    useContext(CurrencyContext) || {};

  useEffect(() => {
    if (!selectedCurrency || !SORTED_CURRENCIES.includes(selectedCurrency)) {
      setSelectedCurrency && setSelectedCurrency("INR");
    }
  }, [selectedCurrency, setSelectedCurrency]);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isCurrencyOpen, setIsCurrencyOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [currentPath, setCurrentPath] = useState("/");
  const [user, setUser] = useState(null);
  const [userOnboardingStatus, setUserOnboardingStatus] = useState(
    USER_ONBOARDING_STATUS,
  );

  const [notifications, setNotifications] = useState([]);

  const [message, setMessage] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  // const [confirmAction, setConfirmAction] = useState(null);

  const notificationRef = useRef(null);
  const currencyRef = useRef(null);
  const profileRef = useRef(null);
  const navigate = useNavigate();

  const [isMobile, setIsMobile] = useState(false);

  const handleProtectedNavigate = (path, userType) => {
    const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";

    if (isLoggedIn) {
      navigate(path, {
        state: { userType },
      });
    } else {
      navigate("/login", {
        state: {
          redirectTo: path,
          userType,
        },
      });
    }
  };

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 700); // Adjust breakpoint to 700px
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const location = useLocation();
  const pathname = location.pathname;

  const showAlert = (msg) => {
    setMessage(msg);
    setShowConfirm(false);
  };

  // Get current page theme
  const getCurrentTheme = () => {
    // Exact match first
    if (PAGE_THEMES[currentPath]) return PAGE_THEMES[currentPath];

    // Dynamic route matching
    if (currentPath.startsWith("/products/"))
      return PAGE_THEMES["/products/:productid"];
    if (currentPath.startsWith("/sellers/"))
      return PAGE_THEMES["/sellers/:sellerId"];
    if (currentPath.startsWith("/franchise/"))
      return PAGE_THEMES["/franchise/:franchiseId"];
    if (currentPath.startsWith("/business/"))
      return PAGE_THEMES["/business/:id"];
    if (currentPath.startsWith("/logistics/"))
      return PAGE_THEMES["/logistics/:id"];
    if (currentPath.startsWith("/services/"))
      return PAGE_THEMES["/services/:id"];

    return PAGE_THEMES.default;
  };

  const theme = getCurrentTheme();

  useEffect(() => {
    setCurrentPath(location.pathname);
  }, [location.pathname]);

  // Fetch notifications only if user is logged in
  useEffect(() => {
    if (!isLoggedIn) {
      return; // Skip if not authenticated
    }

    axios
      .get(`${process.env.REACT_APP_BASE_URL}/notification/send-notification`, {
        withCredentials: true,
      })
      .then((response) => {
        if (Array.isArray(response.data)) {
          setNotifications(response.data); // Set notifications only if response is an array
        } else {
          console.error("Unexpected response format:", response.data);
          setNotifications([]); // Fallback to an empty array
        }
      })
      .catch((error) => {
        console.error("Error fetching notifications:", error);
        setNotifications([]); // Handle errors gracefully
      });
  }, [isLoggedIn]);

  const handleLogout = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/logout`,
        {},
        { withCredentials: true },
      );
      localStorage.removeItem("isLoggedIn");
      navigate("/");
      window.location.reload(); // Reload to ensure full logout state
    } catch (error) {
      console.error("Logout failed:", error);
      showAlert("Failed to log out. Please try again.");
    }
  };

  useEffect(() => {
    const fetchAuthStatus = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/currentuser`,
          { withCredentials: true },
        );
        if (response.status === 200 && response.data.user) {
          setIsLoggedIn(true);
          setUserProfile(response.data.user);
          // set role and badge to export
          userRole = response.data.user.role;
          userBadge = response.data.user.badge;
          // Set currency from backend if available
          if (
            response.data.user.Currency &&
            setSelectedCurrency &&
            SORTED_CURRENCIES.includes(response.data.user.Currency)
          ) {
            setSelectedCurrency(response.data.user.Currency);
            localStorage.setItem(
              "selectedCurrency",
              response.data.user.Currency,
            );
          }
        }
      } catch (error) {
        setIsLoggedIn(false);
      }
    };

    fetchAuthStatus();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);

      if (location.pathname === "/") {
        // On homepage, show search bar only after scrolling 300px
        setShowSearchBar(window.scrollY > 300);
      } else {
        // On all other pages, always show the search bar
        setShowSearchBar(true);
      }
    };

    // Call once to set initial state
    handleScroll();

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [location.pathname]);

  // Update current path
  useEffect(() => {
    setCurrentPath(window.location.pathname);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // For notifications, only close if clicking outside the notificationRef
      if (notificationRef.current) {
        if (!notificationRef.current.contains(event.target)) {
          setIsNotificationOpen(false);
        }
        // If clicking inside notificationRef, don't close (this includes the dropdown)
      }

      if (currencyRef.current && !currencyRef.current.contains(event.target)) {
        setIsCurrencyOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch user data only if user is logged in
  useEffect(() => {
    if (!isLoggedIn) {
      return; // Skip if not authenticated
    }

    axios
      .get(`${process.env.REACT_APP_BASE_URL}/user`, { withCredentials: true })
      .then((res) => {
        setUser(res.data.user || res.data);
        // localStorage.setItem("role", res.data.role || "User"); it is resent in app.js at checkauth
      })
      .catch((err) => {
        // Silently handle error
      });
  }, [isLoggedIn]);

  // Navigation function
  const navigateTo = (path, type) => {
    if (type) {
      navigate(path, { state: { userType: type } });
    } else {
      navigate(path);
    }
  };

  // Get unread notifications count
  const unreadCount =
    notifications && notifications.filter((n) => !n.seen).length;

  // Mark notification as read
  const markAsRead = (id) => {
    setNotifications((prev) =>
      prev.map((notification) =>
        notification.id === id ? { ...notification, seen: true } : notification,
      ),
    );
  };

  // Mark all as read
  const markAllAsRead = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    try {
      await axios.patch(
        `${process.env.REACT_APP_BASE_URL}/notifucation/mark-all-seen`,
        {},
        { withCredentials: true },
      );
      // Update local state
      setNotifications((prev) =>
        prev.map((notification) => ({ ...notification, seen: true })),
      );
      showSuccess("All notifications marked as read");
    } catch (error) {
      console.error("Error marking all as read:", error);
      showError("Failed to mark all notifications as read");
    }
  };

  // Clear all notifications
  const clearAll = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    try {
      await axios.delete(`${process.env.REACT_APP_BASE_URL}/notification`, {
        withCredentials: true,
      });
      // Clear local state
      setNotifications([]);
      showSuccess("All notifications cleared");
    } catch (error) {
      console.error("Error clearing all notifications:", error);
      showError("Failed to clear all notifications");
    }
  };

  // Delete single notification
  const deleteNotification = async (id) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_BASE_URL}/notification/${id}`,
        { withCredentials: true },
      );
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      // showSuccess("Notification cleared"); // Optional: don't spam toasts on swipe
    } catch (error) {
      console.error("Error deleting notification:", error);
      // Revert UI if needed, but for now we assume success for snappiness
      showError("Failed to clear notification");
    }
  };

  const SwipeableNotificationItem = ({
    notification,
    onMarkAsRead,
    onDelete,
  }) => {
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    // Default to 0 translation
    const [translateX, setTranslateX] = useState(0);

    // Minimum distance (in px) to consider it a swipe intent
    const minSwipeDistance = 75;

    const onTouchStart = (e) => {
      setTouchEnd(null);
      setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e) => {
      setTouchEnd(e.targetTouches[0].clientX);
      if (touchStart !== null) {
        const currentTouch = e.targetTouches[0].clientX;
        const diff = currentTouch - touchStart;
        // Allow left swipe only
        if (diff < 0) {
          setTranslateX(diff);
        }
      }
    };

    const onTouchEnd = () => {
      if (!touchStart || !touchEnd) {
        setTranslateX(0);
        return;
      }

      const distance = touchStart - touchEnd;
      const isLeftSwipe = distance > minSwipeDistance;

      if (isLeftSwipe) {
        // Swiped Left
        setTranslateX(-1000); // Throw it off screen
        setTimeout(() => {
          onDelete(notification.id);
        }, 300);
      } else {
        // Snap back
        setTranslateX(0);
      }

      setTouchStart(null);
      setTouchEnd(null);
    };

    return (
      <div className="relative overflow-hidden border-b border-gray-50/50 select-none">
        {/* Background Layer (Red for Delete) */}
        <div className="absolute inset-0 bg-red-500 flex items-center justify-end px-6">
          <span className="text-white font-semibold text-sm">Clear</span>
        </div>

        {/* Foreground Layer (Notification Content) */}
        <div
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onClick={() => onMarkAsRead(notification.id)}
          style={{
            transform: `translateX(${translateX}px)`,
            transition: touchStart ? "none" : "transform 0.3s ease-out",
          }}
          className={`relative z-10 px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors duration-200 group bg-white ${!notification.seen ? "bg-blue-50/40" : ""}`}
        >
          <div className="flex items-start gap-4">
            {!notification.seen && (
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500 rounded-r"></div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline justify-between gap-2 mb-1">
                <h4
                  className={`text-sm font-semibold truncate ${!notification.seen ? "text-gray-900" : "text-gray-700"}`}
                >
                  {notification.title}
                </h4>
                <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">
                  {new Date(notification.createdAt).toLocaleDateString(
                    "en-US",
                    {
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    },
                  )}
                </span>
              </div>
              <p
                className={`text-sm leading-relaxed line-clamp-2 ${!notification.seen ? "text-gray-700" : "text-gray-500"}`}
              >
                {notification.message}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Check if user has completed any onboarding
  const hasAnyOnboarding = Object.values(userOnboardingStatus).some(
    (status) => status === true,
  );

  // Get page-specific onboarding buttons
  const getPageSpecificOnboardingButtons = () => {
    const buttons = [];

    // Show onboarding buttons based on current page
    if (currentPath.includes("/sellers") || currentPath.includes("/products")) {
      if (!userOnboardingStatus.seller) {
        buttons.push({ type: "seller", path: "/preonboarding/seller" });
      }
    }

    if (currentPath.includes("/logistics")) {
      if (!userOnboardingStatus.logistics) {
        buttons.push({ type: "logistics", path: "/logisticsonboarding" });
      }
    }

    if (currentPath.includes("/franchise")) {
      if (!userOnboardingStatus.franchise) {
        buttons.push({
          type: "franchise",
          path: "/businessfranchiseonboarding",
        });
      }
    }

    if (currentPath.includes("/services")) {
      if (!userOnboardingStatus.services) {
        buttons.push({ type: "services", path: "/serviceonboarding" });
      }
    }

    if (currentPath.includes("/business")) {
      if (!userOnboardingStatus.business) {
        buttons.push({
          type: "business",
          path: "/businessfranchiseonboarding",
        });
      }
    }

    return buttons;
  };

  const pageSpecificOnboardingButtons = getPageSpecificOnboardingButtons();

  const isSpecificPath = location.pathname === "/sellerDashBoard";

  const userName = localStorage.getItem("userName");

  const location1 = useLocation();
  const navigate1 = useNavigate();
  // const isDashboardPage = location1.pathname === "/sellerdashboard";
  const isDashboardPage = [
    "/userdashboard",
    "/sellerdashboard",
    "/dashboard/franchise",
    "/dashboard/services",
    "/logisticsDashboard",
    "/dashboard/business-services",
  ].includes(location1.pathname);

  const isLivestreamStart = location.pathname === "/livestream/start";
  // return (
  //   <div className="relative w-full max-w-3xl" ref={searchRef}>
  //     <div className="flex items-center bg-white/95 backdrop-blur-sm border border-white/30 rounded-full shadow-lg overflow-hidden">
  //       {/* Category Selector */}
  //       <div className="relative" ref={categoryRef}>
  //         <button
  //           onClick={() => setIsCategoryOpen(!isCategoryOpen)}
  //           className="flex items-center space-x-2 px-4 py-3 hover:bg-gray-50 transition-colors duration-200 border-r border-gray-200"
  //         >
  //           <SelectedIcon className="h-4 w-4 text-gray-600" />
  //           <span className="text-sm font-medium text-gray-700 hidden sm:block">{selectedCategory.label}</span>
  //           <ChevronDown className="h-4 w-4 text-gray-500" />
  //         </button>

  //         {isCategoryOpen && (
  //           <div className="absolute top-full left-0 mt-1 w-56 bg-white/95 backdrop-blur-md rounded-lg shadow-xl border border-white/20 z-50">
  //             {SEARCH_CATEGORIES.map((category) => {
  //               const CategoryIcon = category.icon
  //               return (
  //                 <button
  //                   key={category.value}
  //                   onClick={() => {
  //                     if (category.isOnboarding) {
  //                       window.location.href = `/${category.value}`
  //                     } else {
  //                       setSelectedCategory(category)
  //                       setIsCategoryOpen(false)
  //                     }
  //                   }}
  //                   className={`w-full flex items-center space-x-3 px-4 py-3 transition-colors duration-150 text-gray-800 ${
  //                     category.isOnboarding
  //                       ? "bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 border-l-4 border-blue-500"
  //                       : "hover:bg-gray-100/80"
  //                   }`}
  //                 >
  //                   <CategoryIcon className={`h-4 w-4 ${category.isOnboarding ? "text-blue-600" : ""}`} />
  //                   <span className={category.isOnboarding ? "font-semibold text-blue-700" : ""}>{category.label}</span>
  //                   {category.isOnboarding && (
  //                     <span className="ml-auto text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full font-medium">
  //                       Join Now
  //                     </span>
  //                   )}
  //                 </button>
  //               )
  //             })}
  //           </div>
  //         )}
  //       </div>

  //       {/* Search Input */}
  //       <div className="flex-1 relative">
  //         <input
  //           type="text"
  //           value={searchQuery}
  //           onChange={(e) => {
  //             setSearchQuery(e.target.value)
  //             setShowSuggestions(e.target.value.length > 0)
  //           }}
  //           onFocus={() => setShowSuggestions(searchQuery.length > 0)}
  //           placeholder={`Search ${selectedCategory.label.toLowerCase()}...`}
  //           className="w-full px-4 py-3 bg-transparent text-gray-900 placeholder-gray-500 focus:outline-none"
  //         />

  //         {/* Search Suggestions */}
  //         {showSuggestions && filteredSuggestions.length > 0 && (
  //           <div className="absolute top-full left-0 right-0 mt-1 bg-white/95 backdrop-blur-md rounded-lg shadow-xl border border-white/20 z-40">
  //             {filteredSuggestions.map((suggestion, index) => (
  //               <button
  //                 key={index}
  //                 onClick={() => {
  //                   setSearchQuery(suggestion)
  //                   setShowSuggestions(false)
  //                 }}
  //                 className="w-full text-left px-4 py-3 hover:bg-gray-100/80 transition-colors duration-150 text-gray-800 border-b border-gray-100/50 last:border-b-0"
  //               >
  //                 <div className="flex items-center space-x-3">
  //                   <Search className="h-4 w-4 text-gray-400" />
  //                   <span>{suggestion}</span>
  //                 </div>
  //               </button>
  //             ))}
  //           </div>
  //         )}
  //       </div>

  //       {/* Search Tools */}
  //       <div className="flex items-center space-x-2 px-3">
  //         {/* Voice Search */}
  //         <button
  //           onClick={handleVoiceSearch}
  //           className={`p-2 rounded-full transition-all duration-200 ${
  //             isListening ? "bg-red-100 text-red-600 animate-pulse" : "hover:bg-gray-100 text-gray-600"
  //           }`}
  //           title="Voice Search"
  //         >
  //           {isListening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
  //         </button>

  //         {/* Image Search */}
  //         <button
  //           onClick={handleImageSearch}
  //           className="p-2 rounded-full hover:bg-gray-100 text-gray-600 transition-all duration-200"
  //           title="Image Search"
  //         >
  //           <Camera className="h-4 w-4" />
  //         </button>

  //         {/* Search Button */}
  //         <button
  //           onClick={() => {
  //             console.log("Searching for:", searchQuery, "in category:", selectedCategory.value)
  //             setShowSuggestions(false)
  //           }}
  //           className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-full transition-all duration-200 font-medium shadow-md"
  //         >
  //           <Search className="h-4 w-4" />
  //         </button>
  //       </div>
  //     </div>
  //   </div>
  // )

  return (
    <header
      className={`fixed left-0 right-0 z-50 transition-all duration-500 ease-in-out  ${isLivestreamStart // Only hide for mobile on livestream start
          ? "hidden" // Hide completely
          : isMobile
            ? `${theme.solid} backdrop-blur-md shadow-lg border-b border-white/10`
            : transparent && !scrolled
              ? `${theme.transparent} backdrop-blur-none`
              : `${theme.solid} backdrop-blur-md border-b border-white/10`
        }`}
      style={{ height: isMobile ? "60px" : "85px" }} // Adjust height for mobile
    >
      <div className="container mx-auto h-full flex items-center px-2 sm:px-1">
        <div
          className={`hidden ${isMobile ? "" : "md:flex"
            } items-center justify-between w-full`}
        >
          {/* Desktop Navigation */}
          {/* Left Side - Logo, Home, and Page-Specific Onboarding */}
          <div className="flex items-center space-x-6">
            {/* Logo */}
            <div
              className="flex items-center cursor-pointer hover:scale-105 transition-transform duration-200"
              onClick={() => navigateTo("/")}
            >
              <img src={merzadologo} alt="MERZADO" className="h-30 w-40" />
            </div>

            {/* Center - Advanced Search Bar */}
            {showSearchBar && (
              <div className="flex-1 max-w-3xl mx-8">
                <AdvancedSearchBar currentPath={currentPath} />
              </div>
            )}

            {/* Page-Specific Onboarding Buttons */}
            {pageSpecificOnboardingButtons.length > 0 && (
              <div className="flex items-center space-x-3">
                {pageSpecificOnboardingButtons.map((button, index) => (
                  <OnboardingButton
                    key={button.type}
                    type={button.type}
                    isOnboarded={userOnboardingStatus[button.type]}
                    onClick={() =>
                      // navigateTo(
                      //   button.path.split("?")[0], // always use the base path
                      //   button.type // pass type as state
                      // )
                      handleProtectedNavigate(
                        button.path.split("?")[0],
                        button.type,
                      )
                    }
                  />
                ))}
              </div>
            )}
          </div>

          {/* Right Side - Controls */}
          <div className="flex items-center text-sm space-x-1">
            {/* Join Merzado button to the left of Google Translate */}
            {(currentPath === "/" ||
              currentPath === "/support" ||
              currentPath === "/payments" ||
              currentPath === "/merzado-services" ||
              currentPath === "/get-to-know" ||
              currentPath === "/blog" ||
              currentPath === "/RFQMainPage" ||
              currentPath === "/livestream" ||
              currentPath === "/TradeShows" ||
              currentPath === "/localsuppliers" ||
              currentPath === "/Tips" ||
              currentPath === "/Subscriptions" ||
              currentPath === "/Tenders" ||
              currentPath === "/leadspagemain") && (
                <a
                  href="/joinmerzado"
                  onClick={(e) => {
                    if (
                      e.button === 0 && // left click
                      !e.metaKey &&
                      !e.ctrlKey &&
                      !e.shiftKey &&
                      !e.altKey
                    ) {
                      e.preventDefault();
                      handleProtectedNavigate("/joinmerzado", "joinmerzado");
                    }
                  }}
                  className="mr-1"
                  style={{
                    height: 44,
                    minHeight: 44,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <OnboardingButton type="homeSpecial" isOnboarded={false} />
                </a>
              )}

            {/* Google Translate */}
            <GoogleTranslate />

            {/* Currency Selector */}
            <div className="relative" ref={currencyRef}>
              <button
                onClick={() => setIsCurrencyOpen(!isCurrencyOpen)}
                className="bg-white/90 backdrop-blur-sm hover:bg-white transition-all duration-200 px-4 py-2 rounded-full text-gray-800 font-medium flex items-center space-x-2 shadow-md"
                style={{
                  height: 44,
                  minHeight: 44,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {/* Only show symbol if it exists */}
                {getCurrencySymbol(selectedCurrency) && (
                  <span>{getCurrencySymbol(selectedCurrency)}</span>
                )}
                <span>{selectedCurrency}</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              {isCurrencyOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white/95 backdrop-blur-md rounded-lg shadow-xl border border-white/20 max-h-60 overflow-y-auto z-50">
                  {SORTED_CURRENCIES.map((currency) => (
                    <button
                      key={currency}
                      onClick={async () => {
                        setSelectedCurrency(currency);
                        setIsCurrencyOpen(false);
                        localStorage.setItem("selectedCurrency", currency);
                        try {
                          await axios.put(
                            `${process.env.REACT_APP_BASE_URL}/user/updateCurrency`,
                            { currency },
                            { withCredentials: true },
                          );
                        } catch (err) {
                          // Optionally handle error
                        }
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100/80 transition-colors duration-150 text-gray-800"
                    >
                      {/* Only show symbol if it exists */}
                      {getCurrencySymbol(currency) && (
                        <span className="mr-1">
                          {getCurrencySymbol(currency)}
                        </span>
                      )}
                      {currency}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Dashboard Button (only if user has onboarded) */}
            {hasAnyOnboarding && (
              <button
                onClick={() => navigateTo("/dashboard")}
                className="bg-white/90 backdrop-blur-sm hover:bg-white transition-all duration-200 px-6 py-2 rounded-full text-gray-800 font-medium shadow-md flex items-center space-x-2"
              >
                <Settings className="h-4 w-4" />
                <span>Dashboard</span>
              </button>
            )}

            {/* Chat Button */}
            <button
              onClick={() => {
                const role = (localStorage.getItem("role") || "").toUpperCase();
                let dashboardPath = "";

                if (role === "USER") {
                  dashboardPath = "/userdashboard?section=chat";
                } else if (role === "SELLER") {
                  dashboardPath = "/sellerdashboard?section=chat";
                } else if (role === "FRANCHISE") {
                  dashboardPath = "/dashboard/franchise?section=chat";
                } else if (role === "SERVICE") {
                  dashboardPath = "/dashboard/services?section=chat";
                } else if (role === "LOGISTICS") {
                  dashboardPath = "/logisticsDashboard?section=chat";
                } else if (role === "BUSINESS" || role === "RESELLER") {
                  dashboardPath = "/dashboard/business-services?section=chat";
                } else {
                  dashboardPath = "/login";
                }

                setIsProfileOpen(false);

                // Refresh and navigate to chat section
                window.location.href = dashboardPath;
              }}
              className="p-3 text-white/90 hover:text-white hover:bg-white/20 rounded-full transition-all duration-200 bg-white/10 backdrop-blur-sm"
              title="Chat Support"
            >
              <MessageSquare className="h-6 w-6" />
              {unreadChatCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full border border-white">
                  {unreadChatCount > 9 ? "9+" : unreadChatCount}
                </span>
              )}
            </button>

            {/* Notification Bell */}
            <div className="relative" ref={notificationRef}>
              <button
                onClick={() => setIsNotificationOpen(!isNotificationOpen)}
                className="relative p-3 text-white/90 hover:text-white hover:bg-white/20 rounded-full transition-all duration-200 bg-white/10 backdrop-blur-sm"
                title="Notifications"
              >
                <IoNotifications size={24} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center animate-pulse shadow-lg">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>

              {isNotificationOpen && (
                <div
                  className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-2xl border border-gray-100 ring-1 ring-black ring-opacity-5 z-50 overflow-hidden transform origin-top-right transition-all duration-200 ease-out"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  {/* Header */}
                  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
                    <h3 className="text-lg font-bold text-gray-800 tracking-tight">
                      Notifications
                    </h3>
                    <div
                      className="flex items-center space-x-4"
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      {unreadCount > 0 && (
                        <button
                          onClick={markAllAsRead}
                          className="text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors uppercase tracking-wider flex items-center group"
                        >
                          <span className="group-hover:underline">
                            Mark all as read
                          </span>
                        </button>
                      )}
                      <button
                        onClick={clearAll}
                        className="text-xs font-semibold text-gray-400 hover:text-red-500 transition-colors uppercase tracking-wider flex items-center group"
                      >
                        <span className="group-hover:underline">Clear All</span>
                      </button>
                    </div>
                  </div>

                  {/* Notifications List */}
                  <div className="max-h-[400px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
                    {notifications.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                        <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                          <IoNotifications
                            size={28}
                            className="text-gray-300"
                          />
                        </div>
                        <h4 className="text-gray-900 font-medium mb-1">
                          No Notifications
                        </h4>
                        <p className="text-sm text-gray-500 max-w-[200px]">
                          You're all caught up! check back later for updates.
                        </p>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-50">
                        {notifications.map((notification) => (
                          <SwipeableNotificationItem
                            key={notification.id}
                            notification={notification}
                            onMarkAsRead={markAsRead}
                            onDelete={deleteNotification}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center space-x-4">
              {!isLoggedIn ? (
                <a
                  href="/login"
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <button type="button" className="login-button">
                    Login
                  </button>
                </a>
              ) : (
                <div className="relative" ref={profileRef}>
                  <button
                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                    className="flex items-center space-x-2 hover:bg-white/20 rounded-full p-2 transition-all duration-200"
                  >
                    <ImagePlaceHolder
                      width={40}
                      height={40}
                      userimage={userprofile?.profilepic}
                      email={user?.email}
                      dropdownOpen={false}
                    />
                    <ChevronDown className="w-4 h-4 text-white/90" />
                  </button>

                  {isProfileOpen && (
                    <div className="absolute right-0 mt-2 min-w-[14rem] max-w-fit bg-white/95 backdrop-blur-md rounded-lg shadow-xl border border-white/20 z-50">
                      {/* User Info */}
                      <div className="p-4 border-b border-gray-100/50">
                        <div className="flex items-center space-x-3">
                          <ImagePlaceHolder
                            width={32}
                            height={32}
                            userimage={userprofile?.profilepic}
                            email={user?.email}
                            dropdownOpen={isProfileOpen}
                          />
                          <div>
                            <p className="font-medium text-gray-900">
                              {userprofile?.name}
                            </p>
                            <p className="text-sm text-gray-500">
                              <p className="text-sm text-gray-500">
                                {user?.username}
                              </p>
                              <p className="ttext-sm text-gray-500 ">
                                {user?.email}
                              </p>
                              {/* <p className="ttext-sm text-white bg-[#1D4ED8] p-1 pt rounded-md">
                                Subscription : {user?.badge} 
                              </p> */}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Menu Items */}
                      <div className="py-2">
                        {/* subscription detail at dropdown - saurabh */}
                        {/* <p className="ttext-sm text-black border-y-2 p-2 text-pretty">
                          Subscription : {user?.badge}
                        </p> */}

                        <button
                          onClick={() => {
                            const role = (
                              localStorage.getItem("role") || ""
                            ).toUpperCase();
                            let dashboardPath = "";

                            // Determine section based on role (buyers use "account-info", others use "profile")
                            const section =
                              role === "USER" ? "account-info" : "profile";

                            if (role === "USER") {
                              dashboardPath = `/userdashboard?section=${section}`;
                            } else if (role === "SELLER") {
                              dashboardPath = `/sellerdashboard?section=${section}`;
                            } else if (role === "FRANCHISE") {
                              dashboardPath = `/dashboard/franchise?section=${section}`;
                            } else if (role === "SERVICE") {
                              dashboardPath = `/dashboard/services?section=${section}`;
                            } else if (role === "LOGISTICS") {
                              dashboardPath = `/logisticsDashboard?section=${section}`;
                            } else if (
                              role === "BUSINESS" ||
                              role === "RESELLER"
                            ) {
                              dashboardPath = `/dashboard/business-services?section=${section}`;
                            } else {
                              dashboardPath = "/";
                            }

                            setIsProfileOpen(false);

                            // Navigate to account/profile section using React Router
                            navigate(dashboardPath);
                          }}
                          className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-gray-100/80 transition-colors duration-150 text-gray-800"
                        >
                          <Settings className="h-5 w-5" />
                          <span>Account</span>
                        </button>

                        <a
                          href="/wishlist"
                          onClick={() => {
                            setIsProfileOpen(false);
                          }}
                          className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-gray-100/80 transition-colors duration-150 text-gray-800"
                        >
                          <Heart className="h-5 w-5" />
                          <span>Wishlist</span>
                        </a>
                        <a
                          href="/following"
                          onClick={() => {
                            setIsProfileOpen(false);
                          }}
                          className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-gray-100/80 transition-colors duration-150 text-gray-800"
                        >
                          <UserPlus className="h-5 w-5" />
                          <span>Following</span>
                        </a>

                        {/* <a
                          href="/personalization"
                          onClick={() => {
                            setIsProfileOpen(false);
                          }}
                          className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-gray-100/80 transition-colors duration-150 text-gray-800"
                        >
                          <UserPlus className="h-5 w-5" />
                          <span className="flex items-center gap-2">
                            Personalization
                            <span className="inline-flex items-center rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-medium px-2 py-0.5">
                              New
                            </span>
                          </span>
                        </a> */}

                        <div className="border-t border-gray-100/50 mt-2 pt-2">
                          <button
                            onClick={handleLogout}
                            className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-red-50/80 transition-colors duration-150 text-red-600"
                          >
                            <LogOut className="h-5 w-5" />
                            <span>Logout</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {!isLivestreamStart && (
          <div
            className={`flex ${isMobile ? "md:hidden" : "hidden"
              } items-center justify-between w-full space-x-1`}
          >
            {/* Mobile Navigation */}
            {/* Mobile Menu Button */}
            {/* <button
            className="text-white/90 hover:text-white transition-colors duration-200 py-2 px-1 shrink-0"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button> */}

            {isDashboardPage && (
              <button
                onClick={() =>
                  navigate1(location1.pathname, {
                    state: { openSidebar: true },
                  })
                }
                className="relative p-2 text-white/90 hover:text-white hover:bg-white/20 rounded-full transition-all duration-200 bg-white/10 backdrop-blur-sm mr-2 block lg:hidden"
              >
                <Menu className="w-4 h-4" />
              </button>
            )}

            <AdvancedSearchBar currentPath={currentPath} className="w-full" />
            {/* Mobile notification */}

            <div className="relative" ref={notificationRef}>
              <button
                onClick={() => setIsNotificationOpen(!isNotificationOpen)}
                className="relative p-2 text-white/90 hover:text-white hover:bg-white/20 rounded-full transition-all duration-200 bg-white/10 backdrop-blur-sm"
                title="Notifications"
              >
                <IoNotifications size={16} />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full h-4 w-4 flex items-center justify-center animate-pulse shadow-sm">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>

              {isNotificationOpen && (
                <div
                  className="absolute right-0 mt-2 w-80 bg-white/95 backdrop-blur-md rounded-lg shadow-xl border border-white/20 z-50"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  {/* Header */}
                  <div className="flex items-center justify-between p-4 border-b border-gray-100/50">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Notifications
                    </h3>
                    <div
                      className="flex items-center space-x-2"
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      {unreadCount > 0 && (
                        <button
                          onClick={markAllAsRead}
                          onMouseDown={(e) => e.stopPropagation()}
                          className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors duration-150"
                        >
                          Mark all read
                        </button>
                      )}
                      <button
                        onClick={clearAll}
                        onMouseDown={(e) => e.stopPropagation()}
                        className="text-sm text-gray-500 hover:text-gray-700 transition-colors duration-150"
                      >
                        Clear all
                      </button>
                    </div>
                  </div>
                  {/* Notifications List */}
                  <div className="max-h-[60vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
                    {notifications.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">
                        <IoNotifications
                          size={48}
                          className="mx-auto mb-2 text-gray-300"
                        />
                        <p>No notifications</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-50">
                        {notifications.map((notification) => (
                          <SwipeableNotificationItem
                            key={notification.id}
                            notification={notification}
                            onMarkAsRead={markAsRead}
                            onDelete={deleteNotification}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="xl:hidden fixed top-[63px] left-0 w-72 h-[calc(100vh-63px)] bg-white/95 backdrop-blur-md shadow-xl border-r border-white/20 z-40 overflow-y-auto">
            <div className="p-4 space-y-4">
              {/* Mobile Navigation Links */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => {
                    navigateTo("/");
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center justify-center space-x-2 bg-gray-100 hover:bg-gray-200 py-3 rounded-lg transition-colors duration-150"
                >
                  <Home className="h-5 w-5 text-gray-600" />
                  <span className="text-gray-800 font-medium">Home</span>
                </button>

                {hasAnyOnboarding ? (
                  <button
                    onClick={() => {
                      navigateTo("/dashboard");
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center justify-center space-x-2 bg-gray-100 hover:bg-gray-200 py-3 rounded-lg transition-colors duration-150"
                  >
                    <Settings className="h-5 w-5 text-gray-600" />
                    <span className="text-gray-800 font-medium">Dashboard</span>
                  </button>
                ) : (
                  pageSpecificOnboardingButtons.length > 0 && (
                    <button
                      onClick={() => {
                        navigateTo(pageSpecificOnboardingButtons[0].path);
                        setMobileMenuOpen(false);
                      }}
                      className="flex items-center justify-center space-x-2 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 py-3 rounded-lg transition-colors duration-150 text-white font-bold"
                    >
                      <Crown className="h-5 w-5" />
                      <span>Join Now</span>
                    </button>
                  )
                )}

                <button
                  onClick={() => {
                    navigateTo("/chat");
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center justify-center space-x-2 bg-gray-100 hover:bg-gray-200 py-3 rounded-lg transition-colors duration-150"
                >
                  <MessageSquare className="h-5 w-5 text-gray-600" />
                  <span className="text-gray-800 font-medium">Chat</span>
                </button>
                <button
                  onClick={() => {
                    navigateTo("/wishlist");
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center justify-center space-x-2 bg-gray-100 hover:bg-gray-200 py-3 rounded-lg transition-colors duration-150"
                >
                  <Heart className="h-5 w-5 text-gray-600" />
                  <span className="text-gray-800 font-medium">Wishlist</span>
                </button>
              </div>

              {/* Mobile Page-Specific Onboarding */}
              {pageSpecificOnboardingButtons.length > 1 && (
                <div className="space-y-2 pt-4 border-t border-gray-200/50">
                  <h4 className="font-medium text-gray-700 mb-2">
                    Available Opportunities
                  </h4>
                  {pageSpecificOnboardingButtons
                    .slice(1)
                    .map((button, index) => (
                      <button
                        key={button.type}
                        onClick={() => {
                          navigateTo(button.path);
                          setMobileMenuOpen(false);
                        }}
                        className="w-full text-left px-4 py-3 rounded-lg transition-all duration-200 text-white font-medium bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                      >
                        {button.type === "logistics" && "Onboard Logistics"}
                        {button.type === "franchise" && "Franchise Partner"}
                        {button.type === "services" && "Service Provider"}
                        {button.type === "business" && "List Your Business"}
                      </button>
                    ))}
                </div>
              )}

              {/* Mobile Controls */}
              <div className="space-y-3 pt-4 border-t border-gray-200/50">
                {/* Currency & Language */}
                <div className="flex items-center justify-between">
                  <span className="text-gray-700 font-medium">Currency:</span>
                  <div
                    className="relative w-full flex justify-end"
                    ref={currencyRef}
                  >
                    <button
                      onClick={() => setIsCurrencyOpen(!isCurrencyOpen)}
                      className="bg-gray-100 border border-gray-300 rounded-lg px-3 py-2 text-gray-800 flex items-center space-x-2"
                      type="button"
                    >
                      {getCurrencySymbol(selectedCurrency) && (
                        <span>{getCurrencySymbol(selectedCurrency)}</span>
                      )}
                      <span>{selectedCurrency}</span>
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    {isCurrencyOpen && (
                      <div className="absolute right-0 mt-2 w-48 bg-white/95 backdrop-blur-md rounded-lg shadow-xl border border-white/20 max-h-60 overflow-y-auto z-50">
                        {SORTED_CURRENCIES.map((currency) => (
                          <button
                            key={currency}
                            onClick={() => {
                              setSelectedCurrency(currency);
                              setIsCurrencyOpen(false);
                            }}
                            className="w-full text-left px-4 py-2 hover:bg-gray-100/80 transition-colors duration-150 text-gray-800 flex items-center"
                          >
                            {getCurrencySymbol(currency) && (
                              <span className="mr-1">
                                {getCurrencySymbol(currency)}
                              </span>
                            )}
                            {currency}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Replace static language select with Google Translate */}
                <div className="flex items-center justify-between">
                  <span className="text-gray-700 font-medium">Language:</span>
                  <div className="flex-1 flex justify-end">
                    <GoogleTranslate />
                  </div>
                </div>
              </div>
              {/* Mobile Logout */}
              <button
                onClick={() => {
                  handleLogout();
                  setMobileMenuOpen(false);
                }}
                className="w-full flex items-center justify-center space-x-2 bg-red-100 hover:bg-red-200 py-3 rounded-lg transition-colors duration-150 text-red-600 font-medium"
              >
                <LogOut className="h-5 w-5" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
